/**
 * Legal Document Templates
 *
 * Template definitions for the legal-doc-creator tool.
 * Supports Employment Agreement, NDA, and Independent Contractor Agreement.
 */

export interface LegalTemplateField {
  id: string;
  label: string;
  labelEs: string;
  type: 'text' | 'date' | 'currency' | 'dropdown' | 'textarea' | 'boolean';
  required: boolean;
  placeholder?: string;
  options?: { value: string; label: string; labelEs: string }[];
  group?: string;
}

export interface LegalTemplateSection {
  id: string;
  title: string;
  titleEs: string;
  content: string;
  contentEs: string;
  conditional?: string;
}

export interface LegalTemplate {
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  category: 'employment' | 'business' | 'general' | 'immigration';
  jurisdiction: 'california' | 'federal';
  fields: LegalTemplateField[];
  sections: LegalTemplateSection[];
  /**
   * Whether this template should display an attorney-review banner in generated
   * output. Defaults to true for the existing 8 static legal templates (preserve
   * current behavior). Defaults to false for user-saved reusable templates and
   * for the freeform 'general-legal-document' path — per Miles decision #4
   * (2026-05-08): attorneys already know they need to review; the banner adds
   * noise on the new general-document path.
   */
  requiresAttorneyReview?: boolean;
  /**
   * True for the freeform general-document path. When set, the generator uses
   * the freeform-legal-drafter system prompt instead of the standard template
   * prompt builder. Existing 8 templates leave this undefined / false.
   */
  isFreeform?: boolean;
  /**
   * Source/scope of the template. Static templates omit this (treated as
   * 'static'). Dynamically-loaded user/firm/global templates carry the scope
   * so the picker can label them and the security layer can enforce write
   * permissions.
   */
  scope?: 'static' | 'user' | 'tenant' | 'global';
  /**
   * English freeform drafter system prompt for saved reusable templates.
   * Populated at save time; used when the user's active language is EN.
   */
  freeformDrafterPromptEn?: string;
  /**
   * Spanish freeform drafter system prompt for saved reusable templates.
   * Auto-generated at save time from the EN pair (or vice versa); used when
   * the user's active language is ES at generation time.
   */
  freeformDrafterPromptEs?: string;
}

// ============================================================================
// Template Definitions
// ============================================================================

const employmentAgreementTemplate: LegalTemplate = {
  id: 'employment-agreement',
  name: 'Employment Agreement',
  nameEs: 'Contrato de Trabajo',
  description: 'A comprehensive California employment agreement covering compensation, benefits, and termination.',
  descriptionEs: 'Un contrato de trabajo completo para California que cubre compensación, beneficios y terminación.',
  category: 'employment',
  jurisdiction: 'california',
  fields: [
    {
      id: 'employer_name',
      label: 'Employer Name',
      labelEs: 'Nombre del Empleador',
      type: 'text',
      required: true,
      placeholder: 'Acme Corporation',
      group: 'employer',
    },
    {
      id: 'employer_address',
      label: 'Employer Address',
      labelEs: 'Dirección del Empleador',
      type: 'text',
      required: true,
      placeholder: '123 Main St, Los Angeles, CA 90001',
      group: 'employer',
    },
    {
      id: 'employee_name',
      label: 'Employee Name',
      labelEs: 'Nombre del Empleado',
      type: 'text',
      required: true,
      placeholder: 'Jane Doe',
      group: 'employee',
    },
    {
      id: 'employee_address',
      label: 'Employee Address',
      labelEs: 'Dirección del Empleado',
      type: 'text',
      required: true,
      placeholder: '456 Oak Ave, San Francisco, CA 94102',
      group: 'employee',
    },
    {
      id: 'job_title',
      label: 'Job Title',
      labelEs: 'Cargo',
      type: 'text',
      required: true,
      placeholder: 'Software Engineer',
      group: 'employment',
    },
    {
      id: 'start_date',
      label: 'Start Date',
      labelEs: 'Fecha de Inicio',
      type: 'date',
      required: true,
      placeholder: '2026-04-01',
      group: 'employment',
    },
    {
      id: 'salary',
      label: 'Annual Salary or Hourly Rate',
      labelEs: 'Salario Anual o Tarifa por Hora',
      type: 'currency',
      required: true,
      placeholder: '$100,000/year or $48/hour',
      group: 'compensation',
    },
    {
      id: 'pay_frequency',
      label: 'Pay Frequency',
      labelEs: 'Frecuencia de Pago',
      type: 'dropdown',
      required: true,
      group: 'compensation',
      options: [
        { value: 'weekly', label: 'Weekly', labelEs: 'Semanal' },
        { value: 'biweekly', label: 'Biweekly (every two weeks)', labelEs: 'Quincenal (cada dos semanas)' },
        { value: 'semimonthly', label: 'Semimonthly (twice per month)', labelEs: 'Semimensual (dos veces al mes)' },
        { value: 'monthly', label: 'Monthly', labelEs: 'Mensual' },
      ],
    },
    {
      id: 'work_schedule',
      label: 'Work Schedule',
      labelEs: 'Horario de Trabajo',
      type: 'text',
      required: false,
      placeholder: 'Monday–Friday, 9am–5pm (40 hours/week)',
      group: 'employment',
    },
    {
      id: 'is_at_will',
      label: 'At-Will Employment',
      labelEs: 'Empleo a Voluntad',
      type: 'boolean',
      required: true,
      group: 'employment',
      options: [
        { value: 'true', label: 'Yes — at-will employment (either party may terminate at any time)', labelEs: 'Sí — empleo a voluntad' },
        { value: 'false', label: 'No — specified term or for-cause only', labelEs: 'No — plazo específico o solo por causa justificada' },
      ],
    },
    {
      id: 'benefits_description',
      label: 'Benefits Description',
      labelEs: 'Descripción de Beneficios',
      type: 'textarea',
      required: false,
      placeholder: 'Health insurance, 401(k) with 4% match, 15 days PTO, dental and vision coverage.',
      group: 'compensation',
    },
    {
      id: 'confidentiality_clause',
      label: 'Include Confidentiality Clause',
      labelEs: 'Incluir Cláusula de Confidencialidad',
      type: 'boolean',
      required: true,
      group: 'terms',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'non_compete',
      label: 'Non-Compete Clause (Note: largely unenforceable in California under SB 699)',
      labelEs: 'Cláusula de No Competencia (Nota: mayormente inaplicable en California)',
      type: 'boolean',
      required: false,
      group: 'terms',
      options: [
        { value: 'true', label: 'Yes (include, with CA enforceability caveat)', labelEs: 'Sí (incluir, con advertencia de aplicabilidad en CA)' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'governing_law',
      label: 'Governing Law',
      labelEs: 'Ley Aplicable',
      type: 'text',
      required: false,
      placeholder: 'State of California',
      group: 'terms',
    },
  ],
  sections: [
    { id: 'parties', title: '1. Parties', titleEs: '1. Partes', content: '', contentEs: '' },
    { id: 'employment-terms', title: '2. Employment Terms', titleEs: '2. Términos de Empleo', content: '', contentEs: '' },
    { id: 'compensation', title: '3. Compensation', titleEs: '3. Compensación', content: '', contentEs: '' },
    { id: 'benefits', title: '4. Benefits', titleEs: '4. Beneficios', content: '', contentEs: '' },
    {
      id: 'confidentiality',
      title: '5. Confidentiality',
      titleEs: '5. Confidencialidad',
      content: '',
      contentEs: '',
      conditional: 'confidentiality_clause',
    },
    { id: 'termination', title: '6. Termination', titleEs: '6. Terminación', content: '', contentEs: '' },
    { id: 'general-provisions', title: '7. General Provisions', titleEs: '7. Disposiciones Generales', content: '', contentEs: '' },
    { id: 'signatures', title: 'Signatures', titleEs: 'Firmas', content: '', contentEs: '' },
  ],
};

const ndaTemplate: LegalTemplate = {
  id: 'nda',
  name: 'Non-Disclosure Agreement (NDA)',
  nameEs: 'Acuerdo de No Divulgación (NDA)',
  description: 'A California non-disclosure agreement for protecting confidential information.',
  descriptionEs: 'Un acuerdo de no divulgación de California para proteger información confidencial.',
  category: 'business',
  jurisdiction: 'california',
  fields: [
    {
      id: 'disclosing_party_name',
      label: 'Disclosing Party Name',
      labelEs: 'Nombre de la Parte Divulgadora',
      type: 'text',
      required: true,
      placeholder: 'Acme Corporation',
      group: 'parties',
    },
    {
      id: 'disclosing_party_address',
      label: 'Disclosing Party Address',
      labelEs: 'Dirección de la Parte Divulgadora',
      type: 'text',
      required: true,
      placeholder: '123 Main St, Los Angeles, CA 90001',
      group: 'parties',
    },
    {
      id: 'receiving_party_name',
      label: 'Receiving Party Name',
      labelEs: 'Nombre de la Parte Receptora',
      type: 'text',
      required: true,
      placeholder: 'John Smith',
      group: 'parties',
    },
    {
      id: 'receiving_party_address',
      label: 'Receiving Party Address',
      labelEs: 'Dirección de la Parte Receptora',
      type: 'text',
      required: true,
      placeholder: '456 Oak Ave, San Francisco, CA 94102',
      group: 'parties',
    },
    {
      id: 'purpose_of_disclosure',
      label: 'Purpose of Disclosure',
      labelEs: 'Propósito de la Divulgación',
      type: 'textarea',
      required: true,
      placeholder: 'Evaluating a potential business partnership or investment opportunity.',
      group: 'terms',
    },
    {
      id: 'confidential_info_definition',
      label: 'Definition of Confidential Information',
      labelEs: 'Definición de Información Confidencial',
      type: 'textarea',
      required: false,
      placeholder: 'All business, technical, financial, and operational information disclosed by the Disclosing Party.',
      group: 'terms',
    },
    {
      id: 'term_duration',
      label: 'Term Duration',
      labelEs: 'Duración del Plazo',
      type: 'text',
      required: true,
      placeholder: '2 years from the effective date',
      group: 'terms',
    },
    {
      id: 'is_mutual',
      label: 'Agreement Type',
      labelEs: 'Tipo de Acuerdo',
      type: 'dropdown',
      required: true,
      group: 'terms',
      options: [
        { value: 'one-way', label: 'One-Way (only Disclosing Party shares confidential info)', labelEs: 'Unilateral (solo la Parte Divulgadora comparte información confidencial)' },
        { value: 'mutual', label: 'Mutual (both parties share confidential info)', labelEs: 'Mutuo (ambas partes comparten información confidencial)' },
      ],
    },
  ],
  sections: [
    { id: 'parties', title: '1. Parties', titleEs: '1. Partes', content: '', contentEs: '' },
    { id: 'purpose', title: '2. Purpose', titleEs: '2. Propósito', content: '', contentEs: '' },
    { id: 'definition', title: '3. Definition of Confidential Information', titleEs: '3. Definición de Información Confidencial', content: '', contentEs: '' },
    { id: 'obligations', title: '4. Obligations of Receiving Party', titleEs: '4. Obligaciones de la Parte Receptora', content: '', contentEs: '' },
    { id: 'exclusions', title: '5. Exclusions from Confidentiality', titleEs: '5. Exclusiones de Confidencialidad', content: '', contentEs: '' },
    { id: 'term', title: '6. Term', titleEs: '6. Plazo', content: '', contentEs: '' },
    { id: 'remedies', title: '7. Remedies', titleEs: '7. Recursos', content: '', contentEs: '' },
    { id: 'general-provisions', title: '8. General Provisions', titleEs: '8. Disposiciones Generales', content: '', contentEs: '' },
    { id: 'signatures', title: 'Signatures', titleEs: 'Firmas', content: '', contentEs: '' },
  ],
};

const contractorAgreementTemplate: LegalTemplate = {
  id: 'contractor-agreement',
  name: 'Independent Contractor Agreement',
  nameEs: 'Contrato de Contratista Independiente',
  description: 'A California AB5-compliant independent contractor agreement.',
  descriptionEs: 'Un contrato de contratista independiente conforme a la Ley AB5 de California.',
  category: 'business',
  jurisdiction: 'california',
  fields: [
    {
      id: 'company_name',
      label: 'Company Name',
      labelEs: 'Nombre de la Empresa',
      type: 'text',
      required: true,
      placeholder: 'Acme Corporation',
      group: 'parties',
    },
    {
      id: 'company_address',
      label: 'Company Address',
      labelEs: 'Dirección de la Empresa',
      type: 'text',
      required: true,
      placeholder: '123 Main St, Los Angeles, CA 90001',
      group: 'parties',
    },
    {
      id: 'contractor_name',
      label: 'Contractor Name',
      labelEs: 'Nombre del Contratista',
      type: 'text',
      required: true,
      placeholder: 'Jane Doe',
      group: 'parties',
    },
    {
      id: 'contractor_address',
      label: 'Contractor Address',
      labelEs: 'Dirección del Contratista',
      type: 'text',
      required: true,
      placeholder: '456 Oak Ave, San Francisco, CA 94102',
      group: 'parties',
    },
    {
      id: 'services_description',
      label: 'Description of Services',
      labelEs: 'Descripción de los Servicios',
      type: 'textarea',
      required: true,
      placeholder: 'Web development services including front-end design, API integration, and testing.',
      group: 'services',
    },
    {
      id: 'compensation_amount',
      label: 'Compensation Amount',
      labelEs: 'Monto de Compensación',
      type: 'currency',
      required: true,
      placeholder: '$5,000 flat fee or $150/hour',
      group: 'compensation',
    },
    {
      id: 'payment_terms',
      label: 'Payment Terms',
      labelEs: 'Términos de Pago',
      type: 'text',
      required: true,
      placeholder: 'Net 30 days after invoice submission',
      group: 'compensation',
    },
    {
      id: 'start_date',
      label: 'Start Date',
      labelEs: 'Fecha de Inicio',
      type: 'date',
      required: true,
      placeholder: '2026-04-01',
      group: 'term',
    },
    {
      id: 'end_date',
      label: 'End Date (or "Ongoing")',
      labelEs: 'Fecha de Finalización (o "Continuo")',
      type: 'text',
      required: false,
      placeholder: '2026-09-30 or Ongoing',
      group: 'term',
    },
    {
      id: 'deliverables',
      label: 'Deliverables',
      labelEs: 'Entregables',
      type: 'textarea',
      required: false,
      placeholder: 'Fully functional web application, source code, documentation, and deployment guide.',
      group: 'services',
    },
    {
      id: 'ip_assignment',
      label: 'IP Assignment (Contractor assigns work product to Company)',
      labelEs: 'Asignación de PI (El Contratista asigna el trabajo a la Empresa)',
      type: 'boolean',
      required: true,
      group: 'terms',
      options: [
        { value: 'true', label: 'Yes — Company owns all work product', labelEs: 'Sí — La Empresa posee todo el trabajo' },
        { value: 'false', label: 'No — Contractor retains IP (license granted to Company)', labelEs: 'No — El Contratista retiene PI (licencia otorgada a la Empresa)' },
      ],
    },
    {
      id: 'insurance_requirements',
      label: 'Insurance Requirements',
      labelEs: 'Requisitos de Seguro',
      type: 'text',
      required: false,
      placeholder: 'General liability insurance of at least $1,000,000 per occurrence.',
      group: 'terms',
    },
  ],
  sections: [
    { id: 'parties', title: '1. Parties', titleEs: '1. Partes', content: '', contentEs: '' },
    { id: 'scope-of-services', title: '2. Scope of Services', titleEs: '2. Alcance de los Servicios', content: '', contentEs: '' },
    { id: 'compensation', title: '3. Compensation and Payment', titleEs: '3. Compensación y Pago', content: '', contentEs: '' },
    { id: 'term', title: '4. Term', titleEs: '4. Plazo', content: '', contentEs: '' },
    {
      id: 'independent-contractor-status',
      title: '5. Independent Contractor Status',
      titleEs: '5. Condición de Contratista Independiente',
      content: '',
      contentEs: '',
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property',
      titleEs: '6. Propiedad Intelectual',
      content: '',
      contentEs: '',
      conditional: 'ip_assignment',
    },
    { id: 'confidentiality', title: '7. Confidentiality', titleEs: '7. Confidencialidad', content: '', contentEs: '' },
    { id: 'termination', title: '8. Termination', titleEs: '8. Terminación', content: '', contentEs: '' },
    { id: 'general-provisions', title: '9. General Provisions', titleEs: '9. Disposiciones Generales', content: '', contentEs: '' },
    { id: 'signatures', title: 'Signatures', titleEs: 'Firmas', content: '', contentEs: '' },
  ],
};

const qmeAdvocacyMemoTemplate: LegalTemplate = {
  id: 'qme-advocacy-memo',
  name: 'QME Advocacy Memo',
  nameEs: 'Memorando de Abogacía QME',
  description: 'A California Workers\' Compensation advocacy memo from the applicant\'s attorney to a Qualified Medical Examiner (QME) prior to evaluation.',
  descriptionEs: 'Un memorando de abogacía de Compensación de Trabajadores de California del abogado del solicitante a un Examinador Médico Calificado (QME) antes de la evaluación.',
  category: 'general',
  jurisdiction: 'california',
  fields: [
    {
      id: 'attorney_name',
      label: 'Attorney Name',
      labelEs: 'Nombre del Abogado',
      type: 'text',
      required: true,
      placeholder: 'Jane Smith, Esq.',
      group: 'attorney',
    },
    {
      id: 'attorney_firm',
      label: 'Law Firm Name',
      labelEs: 'Nombre del Bufete de Abogados',
      type: 'text',
      required: true,
      placeholder: 'Smith & Associates, APC',
      group: 'attorney',
    },
    {
      id: 'attorney_bar_number',
      label: 'State Bar Number',
      labelEs: 'Número de Colegio de Abogados',
      type: 'text',
      required: true,
      placeholder: 'SBN 123456',
      group: 'attorney',
    },
    {
      id: 'attorney_contact',
      label: 'Attorney Contact Info (phone, email, address)',
      labelEs: 'Información de Contacto del Abogado (teléfono, correo, dirección)',
      type: 'text',
      required: true,
      placeholder: '(213) 555-0100 | jsmith@smithlaw.com | 100 Wilshire Blvd, Los Angeles, CA 90017',
      group: 'attorney',
    },
    {
      id: 'applicant_name',
      label: 'Injured Worker Name',
      labelEs: 'Nombre del Trabajador Lesionado',
      type: 'text',
      required: true,
      placeholder: 'Maria Garcia',
      group: 'applicant',
    },
    {
      id: 'applicant_dob',
      label: 'Date of Birth',
      labelEs: 'Fecha de Nacimiento',
      type: 'date',
      required: true,
      placeholder: '1980-05-15',
      group: 'applicant',
    },
    {
      id: 'applicant_occupation',
      label: 'Occupation at Time of Injury',
      labelEs: 'Ocupación al Momento de la Lesión',
      type: 'text',
      required: true,
      placeholder: 'Warehouse Associate',
      group: 'applicant',
    },
    {
      id: 'employer_name',
      label: 'Employer at Time of Injury',
      labelEs: 'Empleador al Momento de la Lesión',
      type: 'text',
      required: true,
      placeholder: 'Acme Logistics, Inc.',
      group: 'applicant',
    },
    {
      id: 'qme_physician_name',
      label: 'QME Physician Name',
      labelEs: 'Nombre del Médico QME',
      type: 'text',
      required: true,
      placeholder: 'Dr. Robert Chen, M.D.',
      group: 'qme',
    },
    {
      id: 'qme_specialty',
      label: 'QME Specialty (e.g. Orthopedic Surgery)',
      labelEs: 'Especialidad del QME (p. ej. Cirugía Ortopédica)',
      type: 'text',
      required: true,
      placeholder: 'Orthopedic Surgery',
      group: 'qme',
    },
    {
      id: 'eval_date',
      label: 'Scheduled Evaluation Date',
      labelEs: 'Fecha Programada de Evaluación',
      type: 'date',
      required: true,
      placeholder: '2026-04-15',
      group: 'qme',
    },
    {
      id: 'injury_dates',
      label: 'Date(s) of Injury',
      labelEs: 'Fecha(s) de Lesión',
      type: 'text',
      required: true,
      placeholder: 'March 10, 2025 (specific) or January 1, 2024 – December 31, 2024 (cumulative)',
      group: 'injury',
    },
    {
      id: 'claim_numbers',
      label: 'Claim Number(s)',
      labelEs: 'Número(s) de Reclamo',
      type: 'text',
      required: true,
      placeholder: 'ADJ12345678',
      group: 'injury',
    },
    {
      id: 'body_parts',
      label: 'Body Parts / Conditions Claimed',
      labelEs: 'Partes del Cuerpo / Condiciones Reclamadas',
      type: 'textarea',
      required: true,
      placeholder: 'Lumbar spine, right knee, and right shoulder.',
      group: 'injury',
    },
    {
      id: 'injury_mechanism',
      label: 'Description of How Injury Occurred',
      labelEs: 'Descripción de Cómo Ocurrió la Lesión',
      type: 'textarea',
      required: true,
      placeholder: 'Applicant was lifting a 75-lb box when they felt a pop in their lower back...',
      group: 'injury',
    },
    {
      id: 'medical_history',
      label: 'Medical Treatment History (providers, surgeries, ongoing treatment)',
      labelEs: 'Historial de Tratamiento Médico (proveedores, cirugías, tratamiento continuo)',
      type: 'textarea',
      required: true,
      placeholder: 'Treated by Dr. Lopez (PTP) from March 2025 – present. MRI on April 5, 2025 showed L4-L5 herniation. Physical therapy x12 sessions.',
      group: 'medical',
    },
    {
      id: 'disputed_issues',
      label: 'Disputed Issues (causation, apportionment, future medical need)',
      labelEs: 'Asuntos en Disputa (causalidad, prorrateo, necesidad médica futura)',
      type: 'textarea',
      required: true,
      placeholder: 'Defense disputes industrial causation. Applicant contends injury is 100% industrial with no pre-existing condition.',
      group: 'legal',
    },
    {
      id: 'attorney_position',
      label: "Attorney's Position / Key Arguments",
      labelEs: 'Posición del Abogado / Argumentos Clave',
      type: 'textarea',
      required: true,
      placeholder: 'Applicant respectfully requests the QME find that all claimed body parts are industrially caused, that no apportionment to non-industrial factors is warranted, and that future medical treatment is reasonably necessary.',
      group: 'legal',
    },
    {
      id: 'medical_records_list',
      label: 'List of Medical Records Being Sent',
      labelEs: 'Lista de Registros Médicos que se Envían',
      type: 'textarea',
      required: false,
      placeholder: '1. Primary Treating Physician records (Dr. Lopez), 3/10/2025 – present\n2. MRI report, 4/5/2025\n3. Physical therapy notes (12 sessions)',
      group: 'records',
    },
    {
      id: 'additional_notes',
      label: 'Additional Notes for the QME',
      labelEs: 'Notas Adicionales para el QME',
      type: 'textarea',
      required: false,
      placeholder: 'Applicant requires a Spanish interpreter. Please allow additional time for the evaluation.',
      group: 'records',
    },
  ],
  sections: [
    { id: 'introduction', title: 'Introduction', titleEs: 'Introducción', content: '', contentEs: '' },
    { id: 'injury_background', title: 'Injury Background', titleEs: 'Antecedentes de la Lesión', content: '', contentEs: '' },
    { id: 'medical_history', title: 'Medical History and Treatment', titleEs: 'Historial Médico y Tratamiento', content: '', contentEs: '' },
    { id: 'body_parts_conditions', title: 'Body Parts and Conditions Claimed', titleEs: 'Partes del Cuerpo y Condiciones Reclamadas', content: '', contentEs: '' },
    { id: 'disputed_issues', title: 'Disputed Issues', titleEs: 'Asuntos en Disputa', content: '', contentEs: '' },
    { id: 'attorney_position', title: "Attorney's Position and Key Arguments", titleEs: 'Posición del Abogado y Argumentos Clave', content: '', contentEs: '' },
    { id: 'medical_records', title: 'Medical Records Submitted', titleEs: 'Registros Médicos Enviados', content: '', contentEs: '' },
    { id: 'closing', title: 'Closing', titleEs: 'Cierre', content: '', contentEs: '' },
  ],
};



const immigrationPreHearingStatementTemplate: LegalTemplate = {
  id: 'immigration-pre-hearing-statement',
  name: 'Immigration Court Pre-Hearing Statement',
  nameEs: 'Declaración Previa a Audiencia de Corte de Inmigración',
  description: 'A pre-hearing statement for immigration court removal proceedings, including asylum, withholding of removal, CAT, and related relief theories.',
  descriptionEs: 'Una declaración previa a audiencia para procedimientos de remoción en corte de inmigración, incluyendo asilo, suspensión de remoción, CAT y teorías relacionadas.',
  category: 'immigration',
  jurisdiction: 'federal',
  fields: [
    { id: 'respondent_name', label: 'Respondent Name', labelEs: 'Nombre del/de la Respondiente', type: 'text', required: true, placeholder: 'Maria Lopez', group: 'parties' },
    { id: 'a_number', label: 'A-Number', labelEs: 'Número A', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'case' },
    { id: 'immigration_court', label: 'Immigration Court', labelEs: 'Corte de Inmigración', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'case' },
    { id: 'hearing_date', label: 'Hearing Date', labelEs: 'Fecha de Audiencia', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'case' },
    { id: 'relief_sought', label: 'Relief Sought', labelEs: 'Alivio Solicitado', type: 'textarea', required: true, placeholder: 'Asylum, withholding of removal, CAT protection, other relief', group: 'relief' },
    { id: 'factual_summary', label: 'Factual Summary', labelEs: 'Resumen de Hechos', type: 'textarea', required: false, placeholder: 'Brief facts supporting the relief sought', group: 'facts' },
    { id: 'legal_theories', label: 'Legal Theories', labelEs: 'Teorías Legales', type: 'textarea', required: false, placeholder: 'Protected ground, nexus, past/future persecution, CAT theory, etc.', group: 'legal' },
    { id: 'evidence', label: 'Evidence to Be Offered', labelEs: 'Evidencia a Presentar', type: 'textarea', required: false, placeholder: 'Declaration, country conditions, identity documents, police/medical records, expert reports, etc.', group: 'evidence' },
    { id: 'witnesses', label: 'Witness List', labelEs: 'Lista de Testigos', type: 'textarea', required: false, placeholder: 'Respondent, family members, experts, corroborating witnesses', group: 'evidence' },
    { id: 'disputed_issues', label: 'Issues in Dispute', labelEs: 'Asuntos en Disputa', type: 'textarea', required: false, placeholder: 'Credibility, nexus, protected ground, likelihood of harm, bars to relief, etc.', group: 'issues' },
    { id: 'estimated_hearing_time', label: 'Estimated Hearing Time', labelEs: 'Tiempo Estimado de Audiencia', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'hearing' },
    { id: 'missing_facts', label: 'Missing Facts / Attorney Review Flags', labelEs: 'Hechos Faltantes / Revisión de Abogado', type: 'textarea', required: false, placeholder: 'A-number, court, IJ, DHS counsel, hearing date, exhibit list, procedural history', group: 'review' },
  ],
  sections: [
    { id: 'caption', title: 'Case Caption and Parties', titleEs: 'Encabezado del Caso y Partes', content: '', contentEs: '' },
    { id: 'relief', title: '1. Relief Sought', titleEs: '1. Alivio Solicitado', content: '', contentEs: '' },
    { id: 'facts', title: '2. Factual Summary', titleEs: '2. Resumen de Hechos', content: '', contentEs: '' },
    { id: 'legal_theories', title: '3. Legal Theories', titleEs: '3. Teorías Legales', content: '', contentEs: '' },
    { id: 'evidence', title: '4. Evidence to Be Offered', titleEs: '4. Evidencia a Presentar', content: '', contentEs: '' },
    { id: 'witnesses', title: '5. Witness List', titleEs: '5. Lista de Testigos', content: '', contentEs: '' },
    { id: 'disputed_issues', title: '6. Issues in Dispute', titleEs: '6. Asuntos en Disputa', content: '', contentEs: '' },
    { id: 'estimated_time', title: '7. Estimated Hearing Time', titleEs: '7. Tiempo Estimado de Audiencia', content: '', contentEs: '' },
    { id: 'attorney_review', title: 'Attorney Review Flags / Missing Facts', titleEs: 'Puntos para Revisión del Abogado / Hechos Faltantes', content: '', contentEs: '' },
  ],
};

const wcabPreHearingStatementTemplate: LegalTemplate = {
  id: 'wcab-pre-hearing-statement',
  name: 'WCAB Pre-Hearing Statement (Rule 10563)',
  nameEs: 'Declaración Previa a Audiencia WCAB (Regla 10563)',
  description: 'A California WCAB Pre-Hearing Statement compliant with Cal. Code Regs., title 8, section 10563.',
  descriptionEs: 'Una declaración previa a audiencia para WCAB conforme a Cal. Code Regs., título 8, sección 10563.',
  category: 'general',
  jurisdiction: 'california',
  fields: [
    { id: 'applicant_name', label: 'Applicant / Injured Worker Name', labelEs: 'Nombre del Solicitante / Trabajador Lesionado', type: 'text', required: true, placeholder: 'Maria Lopez', group: 'parties' },
    { id: 'employer_name', label: 'Employer / Defendant', labelEs: 'Empleador / Demandado', type: 'text', required: true, placeholder: 'ABC Warehouse', group: 'parties' },
    { id: 'insurance_carrier', label: 'Insurance Carrier / Claims Administrator', labelEs: 'Aseguradora / Administrador del Reclamo', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'parties' },
    { id: 'claim_number', label: 'Claim Number / ADJ Number', labelEs: 'Número de Reclamo / ADJ', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'case' },
    { id: 'date_of_injury', label: 'Date of Injury', labelEs: 'Fecha de Lesión', type: 'text', required: true, placeholder: 'March 5, 2025', group: 'case' },
    { id: 'issues_in_dispute', label: 'Issues in Dispute', labelEs: 'Asuntos en Disputa', type: 'textarea', required: true, placeholder: 'Medical treatment, temporary disability, MPN access dispute', group: 'issues' },
    { id: 'stipulations', label: 'Stipulations', labelEs: 'Estipulaciones', type: 'textarea', required: false, placeholder: 'List known stipulations or mark [ATTORNEY REVIEW REQUIRED]', group: 'issues' },
    { id: 'evidence', label: 'Evidence to Be Presented', labelEs: 'Evidencia a Presentar', type: 'textarea', required: false, placeholder: 'Medical records, wage records, injury reports, correspondence', group: 'evidence' },
    { id: 'witnesses', label: 'Witnesses Expected to Testify', labelEs: 'Testigos Esperados', type: 'textarea', required: false, placeholder: 'Applicant, supervisor, coworker, PTP, QME, etc.', group: 'evidence' },
    { id: 'estimated_hearing_time', label: 'Estimated Time for Hearing', labelEs: 'Tiempo Estimado para la Audiencia', type: 'text', required: false, placeholder: '[ATTORNEY REVIEW REQUIRED]', group: 'hearing' },
    { id: 'missing_facts', label: 'Missing Facts / Attorney Review Flags', labelEs: 'Hechos Faltantes / Revisión de Abogado', type: 'textarea', required: false, placeholder: 'Claim number, insurance carrier, supervisor name, medical records', group: 'review' },
  ],
  sections: [
    { id: 'caption', title: 'Case Caption and Parties', titleEs: 'Encabezado del Caso y Partes', content: '', contentEs: '' },
    { id: 'identity', title: '1. Identity of All Parties', titleEs: '1. Identidad de Todas las Partes', content: '', contentEs: '' },
    { id: 'issues', title: '2. Issues in Dispute', titleEs: '2. Asuntos en Disputa', content: '', contentEs: '' },
    { id: 'stipulations', title: '3. Stipulations', titleEs: '3. Estipulaciones', content: '', contentEs: '' },
    { id: 'evidence', title: '4. Evidence to Be Presented', titleEs: '4. Evidencia a Presentar', content: '', contentEs: '' },
    { id: 'witnesses', title: '5. Witnesses Expected to Testify', titleEs: '5. Testigos Esperados', content: '', contentEs: '' },
    { id: 'estimated_time', title: '6. Estimated Time for Hearing', titleEs: '6. Tiempo Estimado para la Audiencia', content: '', contentEs: '' },
    { id: 'attorney_review', title: 'Attorney Review Flags / Missing Facts', titleEs: 'Puntos para Revisión del Abogado / Hechos Faltantes', content: '', contentEs: '' },
  ],
};

// ============================================================================
// I-589 Asylum Application Template
// ============================================================================

const i589AsylumTemplate: LegalTemplate = {
  id: 'i-589-asylum',
  name: 'I-589 Asylum Application',
  nameEs: 'Solicitud de Asilo I-589',
  description:
    'USCIS Form I-589: Application for Asylum and for Withholding of Removal. Fills the official USCIS PDF form.',
  descriptionEs:
    'Formulario USCIS I-589: Solicitud de Asilo y de Suspensión de Remoción. Completa el formulario PDF oficial de USCIS.',
  category: 'immigration',
  jurisdiction: 'federal',
  fields: [
    // ---- Part A.I: Applicant Identity ----
    {
      id: 'last_name',
      label: 'Last Name',
      labelEs: 'Apellido',
      type: 'text',
      required: true,
      placeholder: 'Smith',
      group: 'partAI_identity',
    },
    {
      id: 'first_name',
      label: 'First Name',
      labelEs: 'Nombre',
      type: 'text',
      required: true,
      placeholder: 'Maria',
      group: 'partAI_identity',
    },
    {
      id: 'middle_name',
      label: 'Middle Name',
      labelEs: 'Segundo Nombre',
      type: 'text',
      required: false,
      placeholder: 'Elena',
      group: 'partAI_identity',
    },
    {
      id: 'aliases',
      label: 'Other Names Used (aliases, maiden name)',
      labelEs: 'Otros Nombres Usados (alias, nombre de soltera)',
      type: 'text',
      required: false,
      placeholder: 'Maria Garcia (maiden name)',
      group: 'partAI_identity',
    },
    {
      id: 'dob',
      label: 'Date of Birth (MM/DD/YYYY)',
      labelEs: 'Fecha de Nacimiento (MM/DD/AAAA)',
      type: 'date',
      required: true,
      placeholder: '01/15/1990',
      group: 'partAI_identity',
    },
    {
      id: 'city_of_birth',
      label: 'City/Town of Birth',
      labelEs: 'Ciudad/Pueblo de Nacimiento',
      type: 'text',
      required: true,
      placeholder: 'Guatemala City',
      group: 'partAI_identity',
    },
    {
      id: 'country_of_birth',
      label: 'Country of Birth',
      labelEs: 'País de Nacimiento',
      type: 'text',
      required: true,
      placeholder: 'Guatemala',
      group: 'partAI_identity',
    },
    {
      id: 'nationality',
      label: 'Present Nationality/Citizenship',
      labelEs: 'Nacionalidad/Ciudadanía Actual',
      type: 'text',
      required: true,
      placeholder: 'Guatemalan',
      group: 'partAI_identity',
    },
    {
      id: 'race_ethnicity',
      label: 'Race, Ethnic, or Tribal Group',
      labelEs: 'Grupo Racial, Étnico o Tribal',
      type: 'text',
      required: false,
      placeholder: 'Mayan / Mestizo',
      group: 'partAI_identity',
    },
    {
      id: 'religion',
      label: 'Religion',
      labelEs: 'Religión',
      type: 'text',
      required: false,
      placeholder: 'Catholic',
      group: 'partAI_identity',
    },
    {
      id: 'gender',
      label: 'Gender',
      labelEs: 'Género',
      type: 'dropdown',
      required: true,
      group: 'partAI_identity',
      options: [
        { value: 'male', label: 'Male', labelEs: 'Masculino' },
        { value: 'female', label: 'Female', labelEs: 'Femenino' },
      ],
    },
    {
      id: 'marital_status',
      label: 'Marital Status',
      labelEs: 'Estado Civil',
      type: 'dropdown',
      required: true,
      group: 'partAI_identity',
      options: [
        { value: 'single', label: 'Single', labelEs: 'Soltero/a' },
        { value: 'married', label: 'Married', labelEs: 'Casado/a' },
        { value: 'divorced', label: 'Divorced', labelEs: 'Divorciado/a' },
        { value: 'widowed', label: 'Widowed', labelEs: 'Viudo/a' },
      ],
    },
    // ---- Part A.I: Immigration Status ----
    {
      id: 'a_number',
      label: 'Alien Registration Number (A-Number, if any)',
      labelEs: 'Número de Registro de Extranjero (Número A, si tiene)',
      type: 'text',
      required: false,
      placeholder: 'A-012345678',
      group: 'partAI_immigration',
    },
    {
      id: 'ssn',
      label: 'Social Security Number (if any)',
      labelEs: 'Número de Seguro Social (si tiene)',
      type: 'text',
      required: false,
      placeholder: '123-45-6789',
      group: 'partAI_immigration',
    },
    {
      id: 'i94_number',
      label: 'I-94 Arrival/Departure Record Number',
      labelEs: 'Número de Registro de Llegada/Salida I-94',
      type: 'text',
      required: false,
      placeholder: '12345678901',
      group: 'partAI_immigration',
    },
    {
      id: 'last_entry_date',
      label: 'Date of Last Arrival in US (MM/DD/YYYY)',
      labelEs: 'Fecha de Última Llegada a EE.UU. (MM/DD/AAAA)',
      type: 'date',
      required: true,
      placeholder: '03/15/2023',
      group: 'partAI_immigration',
    },
    {
      id: 'last_entry_place',
      label: 'Place of Last Entry into US',
      labelEs: 'Lugar de Última Entrada a EE.UU.',
      type: 'text',
      required: true,
      placeholder: 'San Diego, CA',
      group: 'partAI_immigration',
    },
    {
      id: 'immigration_status',
      label: 'Immigration Status at Last Entry',
      labelEs: 'Estatus Migratorio en Última Entrada',
      type: 'text',
      required: true,
      placeholder: 'Parolee / Visitor / EWI',
      group: 'partAI_immigration',
    },
    {
      id: 'authorized_stay_expiry',
      label: 'Authorized Stay Expiration Date (MM/DD/YYYY, or N/A)',
      labelEs: 'Fecha de Vencimiento de Estadía Autorizada (MM/DD/AAAA, o N/A)',
      type: 'text',
      required: false,
      placeholder: '06/15/2023 or N/A',
      group: 'partAI_immigration',
    },
    // ---- Part A.I: Address ----
    {
      id: 'residence_street',
      label: 'Current Residence Street Address',
      labelEs: 'Dirección de Calle de Residencia Actual',
      type: 'text',
      required: true,
      placeholder: '123 Main St Apt 4B',
      group: 'partAI_address',
    },
    {
      id: 'residence_city',
      label: 'City',
      labelEs: 'Ciudad',
      type: 'text',
      required: true,
      placeholder: 'Los Angeles',
      group: 'partAI_address',
    },
    {
      id: 'residence_state',
      label: 'State',
      labelEs: 'Estado',
      type: 'text',
      required: true,
      placeholder: 'CA',
      group: 'partAI_address',
    },
    {
      id: 'residence_zip',
      label: 'ZIP Code',
      labelEs: 'Código Postal',
      type: 'text',
      required: true,
      placeholder: '90001',
      group: 'partAI_address',
    },
    {
      id: 'residence_phone',
      label: 'Daytime Phone Number',
      labelEs: 'Número de Teléfono Diurno',
      type: 'text',
      required: false,
      placeholder: '(213) 555-0100',
      group: 'partAI_address',
    },
    // ---- Part A.II: Spouse ----
    {
      id: 'spouse_last_name',
      label: 'Spouse Last Name (if married)',
      labelEs: 'Apellido del Cónyuge (si está casado/a)',
      type: 'text',
      required: false,
      placeholder: 'Smith',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_first_name',
      label: 'Spouse First Name',
      labelEs: 'Nombre del Cónyuge',
      type: 'text',
      required: false,
      placeholder: 'Juan',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_middle_name',
      label: 'Spouse Middle Name',
      labelEs: 'Segundo Nombre del Cónyuge',
      type: 'text',
      required: false,
      placeholder: 'Carlos',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_dob',
      label: 'Spouse Date of Birth (MM/DD/YYYY)',
      labelEs: 'Fecha de Nacimiento del Cónyuge (MM/DD/AAAA)',
      type: 'date',
      required: false,
      placeholder: '05/20/1988',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_nationality',
      label: 'Spouse Nationality/Citizenship',
      labelEs: 'Nacionalidad/Ciudadanía del Cónyuge',
      type: 'text',
      required: false,
      placeholder: 'Guatemalan',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_a_number',
      label: 'Spouse A-Number (if any)',
      labelEs: 'Número A del Cónyuge (si tiene)',
      type: 'text',
      required: false,
      placeholder: 'A-012345679',
      group: 'partAII_spouse',
    },
    {
      id: 'spouse_in_us',
      label: 'Is Spouse Currently in the US?',
      labelEs: '¿Está el Cónyuge Actualmente en EE.UU.?',
      type: 'boolean',
      required: false,
      group: 'partAII_spouse',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No — still in home country or third country', labelEs: 'No — aún en país de origen o tercer país' },
      ],
    },
    // ---- Part A.II: Children ----
    {
      id: 'num_children',
      label: 'Total Number of Children',
      labelEs: 'Número Total de Hijos',
      type: 'text',
      required: false,
      placeholder: '2',
      group: 'partAII_children',
    },
    {
      id: 'child1_last_name',
      label: 'Child 1 — Last Name',
      labelEs: 'Hijo/a 1 — Apellido',
      type: 'text',
      required: false,
      placeholder: 'Smith',
      group: 'partAII_children',
    },
    {
      id: 'child1_first_name',
      label: 'Child 1 — First Name',
      labelEs: 'Hijo/a 1 — Nombre',
      type: 'text',
      required: false,
      placeholder: 'Ana',
      group: 'partAII_children',
    },
    {
      id: 'child1_dob',
      label: 'Child 1 — Date of Birth (MM/DD/YYYY)',
      labelEs: 'Hijo/a 1 — Fecha de Nacimiento (MM/DD/AAAA)',
      type: 'date',
      required: false,
      placeholder: '06/01/2015',
      group: 'partAII_children',
    },
    {
      id: 'child1_nationality',
      label: 'Child 1 — Nationality',
      labelEs: 'Hijo/a 1 — Nacionalidad',
      type: 'text',
      required: false,
      placeholder: 'Guatemalan',
      group: 'partAII_children',
    },
    {
      id: 'child1_in_us',
      label: 'Child 1 — Currently in US?',
      labelEs: 'Hijo/a 1 — ¿Actualmente en EE.UU.?',
      type: 'boolean',
      required: false,
      group: 'partAII_children',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'child2_last_name',
      label: 'Child 2 — Last Name',
      labelEs: 'Hijo/a 2 — Apellido',
      type: 'text',
      required: false,
      placeholder: 'Smith',
      group: 'partAII_children',
    },
    {
      id: 'child2_first_name',
      label: 'Child 2 — First Name',
      labelEs: 'Hijo/a 2 — Nombre',
      type: 'text',
      required: false,
      placeholder: 'Luis',
      group: 'partAII_children',
    },
    {
      id: 'child2_dob',
      label: 'Child 2 — Date of Birth (MM/DD/YYYY)',
      labelEs: 'Hijo/a 2 — Fecha de Nacimiento (MM/DD/AAAA)',
      type: 'date',
      required: false,
      placeholder: '09/14/2017',
      group: 'partAII_children',
    },
    {
      id: 'child2_nationality',
      label: 'Child 2 — Nationality',
      labelEs: 'Hijo/a 2 — Nacionalidad',
      type: 'text',
      required: false,
      placeholder: 'Guatemalan',
      group: 'partAII_children',
    },
    {
      id: 'child3_last_name',
      label: 'Child 3 — Last Name',
      labelEs: 'Hijo/a 3 — Apellido',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child3_first_name',
      label: 'Child 3 — First Name',
      labelEs: 'Hijo/a 3 — Nombre',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child3_dob',
      label: 'Child 3 — Date of Birth (MM/DD/YYYY)',
      labelEs: 'Hijo/a 3 — Fecha de Nacimiento (MM/DD/AAAA)',
      type: 'date',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child3_nationality',
      label: 'Child 3 — Nationality',
      labelEs: 'Hijo/a 3 — Nacionalidad',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child4_last_name',
      label: 'Child 4 — Last Name',
      labelEs: 'Hijo/a 4 — Apellido',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child4_first_name',
      label: 'Child 4 — First Name',
      labelEs: 'Hijo/a 4 — Nombre',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child4_dob',
      label: 'Child 4 — Date of Birth (MM/DD/YYYY)',
      labelEs: 'Hijo/a 4 — Fecha de Nacimiento (MM/DD/AAAA)',
      type: 'date',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    {
      id: 'child4_nationality',
      label: 'Child 4 — Nationality',
      labelEs: 'Hijo/a 4 — Nacionalidad',
      type: 'text',
      required: false,
      placeholder: '',
      group: 'partAII_children',
    },
    // ---- Part A.III: Background ----
    {
      id: 'last_country_before_us',
      label: 'Last Country of Habitual Residence Before US',
      labelEs: 'Último País de Residencia Habitual Antes de EE.UU.',
      type: 'text',
      required: false,
      placeholder: 'Mexico',
      group: 'partAIII_background',
    },
    {
      id: 'travel_history',
      label: 'Countries Traveled Through to Reach US (list all)',
      labelEs: 'Países por los que Viajó para Llegar a EE.UU. (lista todos)',
      type: 'textarea',
      required: false,
      placeholder: 'Mexico (January 2023), Guatemala (December 2022)',
      group: 'partAIII_background',
    },
    {
      id: 'previous_applications',
      label: 'Have you previously applied for asylum in any country (including US)?',
      labelEs: '¿Ha solicitado asilo anteriormente en algún país (incluido EE.UU.)?',
      type: 'boolean',
      required: false,
      group: 'partAIII_background',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'previous_applications_detail',
      label: 'If yes, explain previous applications',
      labelEs: 'Si es sí, explique las solicitudes anteriores',
      type: 'textarea',
      required: false,
      placeholder: 'Applied in Mexico in 2022; case denied.',
      group: 'partAIII_background',
    },
    {
      id: 'education_history',
      label: 'Education History (school name, dates, level completed)',
      labelEs: 'Historial Educativo (nombre de la escuela, fechas, nivel completado)',
      type: 'textarea',
      required: false,
      placeholder: 'Universidad de San Carlos, Guatemala, 2008-2012, Bachelor\'s degree in Education',
      group: 'partAIII_background',
    },
    {
      id: 'employment_history',
      label: 'Employment History (last 5 years: employer, dates, occupation)',
      labelEs: 'Historial Laboral (últimos 5 años: empleador, fechas, ocupación)',
      type: 'textarea',
      required: false,
      placeholder: 'Teacher at Escuela Nacional, Guatemala City, 2019-2022',
      group: 'partAIII_background',
    },
    // ---- Part B: Persecution Claim ----
    {
      id: 'persecution_grounds',
      label: 'Grounds for Asylum Claim (check all that apply)',
      labelEs: 'Fundamentos de la Solicitud de Asilo (marque todos los que apliquen)',
      type: 'dropdown',
      required: true,
      group: 'partB_persecution',
      options: [
        { value: 'race', label: 'Race', labelEs: 'Raza' },
        { value: 'religion', label: 'Religion', labelEs: 'Religión' },
        { value: 'nationality', label: 'Nationality', labelEs: 'Nacionalidad' },
        { value: 'political_opinion', label: 'Political Opinion', labelEs: 'Opinión Política' },
        { value: 'social_group', label: 'Particular Social Group', labelEs: 'Grupo Social Particular' },
        { value: 'torture', label: 'Torture (CAT claim)', labelEs: 'Tortura (reclamo CAT)' },
        { value: 'multiple', label: 'Multiple grounds (describe in narrative)', labelEs: 'Múltiples fundamentos (describir en narrativa)' },
      ],
    },
    {
      id: 'persecution_narrative',
      label: 'Part B — Persecution Narrative (describe why you fear persecution: what happened, who harmed you, why, and what you fear if returned)',
      labelEs: 'Parte B — Narrativa de Persecución (describa por qué teme persecución: qué pasó, quién le hizo daño, por qué, y qué teme si regresa)',
      type: 'textarea',
      required: true,
      placeholder: 'I am applying for asylum because I fear persecution in my home country based on [grounds]. Beginning in [year], I experienced [specific incidents]. The perpetrators were [who]. I believe I was targeted because [reason]. I fear that if I return to [country], I will [specific harm feared]...',
      group: 'partB_persecution',
    },
    {
      id: 'persecution_past',
      label: 'Have you suffered past persecution?',
      labelEs: '¿Ha sufrido persecución en el pasado?',
      type: 'boolean',
      required: false,
      group: 'partB_persecution',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'persecution_fear_return',
      label: 'Do you fear persecution if returned to your home country?',
      labelEs: '¿Teme persecución si regresa a su país de origen?',
      type: 'boolean',
      required: false,
      group: 'partB_persecution',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    // ---- Part C: Additional Info ----
    {
      id: 'other_country_claim',
      label: 'Part C — Are you filing for withholding of removal to any other country?',
      labelEs: 'Parte C — ¿Está solicitando suspensión de remoción a algún otro país?',
      type: 'boolean',
      required: false,
      group: 'partC_additional',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    {
      id: 'other_country_detail',
      label: 'If yes, which country and why',
      labelEs: 'Si es sí, ¿cuál país y por qué?',
      type: 'textarea',
      required: false,
      placeholder: 'Mexico — fear of persecution by cartel that has operations in border cities',
      group: 'partC_additional',
    },
    {
      id: 'immigration_proceedings',
      label: 'Are you currently in immigration proceedings?',
      labelEs: '¿Está actualmente en procedimientos migratorios?',
      type: 'boolean',
      required: false,
      group: 'partC_additional',
      options: [
        { value: 'true', label: 'Yes', labelEs: 'Sí' },
        { value: 'false', label: 'No', labelEs: 'No' },
      ],
    },
    // ---- Part D / E: Attorney / Preparer ----
    {
      id: 'attorney_name',
      label: 'Attorney / Preparer Name (if applicable)',
      labelEs: 'Nombre del Abogado / Preparador (si aplica)',
      type: 'text',
      required: false,
      placeholder: 'Jane Smith, Esq.',
      group: 'partE_attorney',
    },
    {
      id: 'attorney_bar_number',
      label: 'Attorney State Bar Number',
      labelEs: 'Número de Colegio de Abogados del Abogado',
      type: 'text',
      required: false,
      placeholder: 'SBN 234567',
      group: 'partE_attorney',
    },
    {
      id: 'attorney_street',
      label: 'Attorney Street Address',
      labelEs: 'Dirección del Abogado',
      type: 'text',
      required: false,
      placeholder: '456 Wilshire Blvd, Suite 200',
      group: 'partE_attorney',
    },
    {
      id: 'attorney_city',
      label: 'Attorney City',
      labelEs: 'Ciudad del Abogado',
      type: 'text',
      required: false,
      placeholder: 'Los Angeles',
      group: 'partE_attorney',
    },
    {
      id: 'attorney_state',
      label: 'Attorney State',
      labelEs: 'Estado del Abogado',
      type: 'text',
      required: false,
      placeholder: 'CA',
      group: 'partE_attorney',
    },
    {
      id: 'attorney_zip',
      label: 'Attorney ZIP Code',
      labelEs: 'Código Postal del Abogado',
      type: 'text',
      required: false,
      placeholder: '90010',
      group: 'partE_attorney',
    },
  ],
  sections: [
    {
      id: 'partAI',
      title: 'Part A.I — Applicant Information',
      titleEs: 'Parte A.I — Información del Solicitante',
      content: '',
      contentEs: '',
    },
    {
      id: 'partAII_spouse',
      title: 'Part A.II — Spouse Information',
      titleEs: 'Parte A.II — Información del Cónyuge',
      content: '',
      contentEs: '',
      conditional: 'spouse_last_name',
    },
    {
      id: 'partAII_children',
      title: 'Part A.II — Children',
      titleEs: 'Parte A.II — Hijos/as',
      content: '',
      contentEs: '',
      conditional: 'num_children',
    },
    {
      id: 'partAIII',
      title: 'Part A.III — Background Information',
      titleEs: 'Parte A.III — Información de Antecedentes',
      content: '',
      contentEs: '',
    },
    {
      id: 'partB',
      title: 'Part B — Persecution Claim',
      titleEs: 'Parte B — Reclamación de Persecución',
      content: '',
      contentEs: '',
    },
    {
      id: 'partC',
      title: 'Part C — Additional Claims',
      titleEs: 'Parte C — Reclamaciones Adicionales',
      content: '',
      contentEs: '',
    },
    {
      id: 'partD',
      title: 'Part D — Signature and Declaration',
      titleEs: 'Parte D — Firma y Declaración',
      content: '',
      contentEs: '',
    },
    {
      id: 'partE',
      title: 'Part E — Attorney / Preparer Information',
      titleEs: 'Parte E — Información del Abogado / Preparador',
      content: '',
      contentEs: '',
    },
  ],
};

// ============================================================================
// Demand Letter Template
// ============================================================================

const demandLetterTemplate: LegalTemplate = {
  id: 'demand-letter',
  name: 'Demand Letter',
  nameEs: 'Carta de Demanda',
  description: 'A formal demand letter requesting action, payment, or resolution of a dispute',
  descriptionEs: 'Una carta de demanda formal solicitando acción, pago o resolución de una disputa',
  category: 'general',
  jurisdiction: 'california',
  fields: [
    {
      id: 'sender_name',
      label: 'Sender Name (Your Name / Law Firm)',
      labelEs: 'Nombre del Remitente (Su Nombre / Bufete)',
      type: 'text',
      required: true,
      placeholder: 'e.g. Smith Legal Group',
    },
    {
      id: 'sender_address',
      label: 'Sender Address',
      labelEs: 'Dirección del Remitente',
      type: 'text',
      required: true,
      placeholder: 'e.g. 123 Legal Ave, Suite 200, Los Angeles, CA 90001',
    },
    {
      id: 'recipient_name',
      label: 'Recipient Name (Person/Company)',
      labelEs: 'Nombre del Destinatario (Persona/Empresa)',
      type: 'text',
      required: true,
      placeholder: 'e.g. ABC Insurance Company',
    },
    {
      id: 'recipient_address',
      label: 'Recipient Address',
      labelEs: 'Dirección del Destinatario',
      type: 'text',
      required: true,
      placeholder: 'e.g. 456 Corporate Blvd, New York, NY 10001',
    },
    {
      id: 'client_name',
      label: 'Client Name (if on behalf of someone)',
      labelEs: 'Nombre del Cliente (si es en representación)',
      type: 'text',
      required: false,
      placeholder: 'e.g. Maria Garcia',
    },
    {
      id: 'subject',
      label: 'Subject / Re: Line',
      labelEs: 'Asunto / Re:',
      type: 'text',
      required: true,
      placeholder: 'e.g. Demand for Payment — Breach of Contract',
    },
    {
      id: 'demand_type',
      label: 'Type of Demand',
      labelEs: 'Tipo de Demanda',
      type: 'dropdown',
      required: true,
      options: [
        { label: 'Payment / Debt Collection', labelEs: 'Pago / Cobro de Deuda', value: 'payment' },
        { label: 'Breach of Contract', labelEs: 'Incumplimiento de Contrato', value: 'breach_contract' },
        { label: 'Property Damage', labelEs: 'Daño a Propiedad', value: 'property_damage' },
        { label: 'Personal Injury', labelEs: 'Lesión Personal', value: 'personal_injury' },
        { label: 'Employment / Wage Dispute', labelEs: 'Disputa Laboral / Salarial', value: 'employment' },
        { label: 'Insurance Claim', labelEs: 'Reclamo de Seguro', value: 'insurance' },
        { label: 'Cease and Desist', labelEs: 'Cese y Desista', value: 'cease_desist' },
        { label: 'Other', labelEs: 'Otro', value: 'other' },
      ],
    },
    {
      id: 'amount_demanded',
      label: 'Amount Demanded (if monetary)',
      labelEs: 'Monto Demandado (si es monetario)',
      type: 'text',
      required: false,
      placeholder: 'e.g. $15,000.00',
    },
    {
      id: 'facts',
      label: 'Statement of Facts (what happened)',
      labelEs: 'Declaración de Hechos (qué ocurrió)',
      type: 'textarea',
      required: true,
      placeholder: 'Describe the events leading to this demand...',
    },
    {
      id: 'legal_basis',
      label: 'Legal Basis (statutes, contract terms, etc.)',
      labelEs: 'Fundamento Legal (estatutos, términos del contrato, etc.)',
      type: 'textarea',
      required: false,
      placeholder: 'e.g. California Civil Code §1542, breach of contract terms in Section 4.2',
    },
    {
      id: 'deadline_days',
      label: 'Response Deadline (business days)',
      labelEs: 'Plazo de Respuesta (días hábiles)',
      type: 'text',
      required: true,
      placeholder: 'e.g. 15',
    },
    {
      id: 'consequences',
      label: 'Consequences if Demand Not Met',
      labelEs: 'Consecuencias si no se Cumple la Demanda',
      type: 'textarea',
      required: false,
      placeholder: 'e.g. We will pursue all available legal remedies including filing a civil complaint...',
    },
    {
      id: 'governing_law',
      label: 'Governing Law (State)',
      labelEs: 'Ley Aplicable (Estado)',
      type: 'text',
      required: true,
      placeholder: 'e.g. California',
    },
  ],
  sections: [
    { id: 'header', title: 'Letter Header', titleEs: 'Encabezado', content: '', contentEs: '' },
    { id: 'facts', title: 'Statement of Facts', titleEs: 'Declaración de Hechos', content: '', contentEs: '' },
    { id: 'legal-basis', title: 'Legal Basis', titleEs: 'Fundamento Legal', content: '', contentEs: '' },
    { id: 'demand', title: 'Demand', titleEs: 'Demanda', content: '', contentEs: '' },
    { id: 'deadline', title: 'Deadline & Consequences', titleEs: 'Plazo y Consecuencias', content: '', contentEs: '' },
    { id: 'closing', title: 'Closing', titleEs: 'Cierre', content: '', contentEs: '' },
  ],
};

// ============================================================================
// General Document (freeform) — 9th template (2026-05-08 Miles approval)
//
// This template intentionally has a minimal field schema. The drafter
// infers structure, section order, and the conventional document name
// directly from the user's stated purpose. See legalDocCreator's
// generateStep for the freeform drafter branch (Layer B).
// ============================================================================

const generalLegalDocumentTemplate: LegalTemplate = {
  id: 'general-legal-document',
  name: 'General Document',
  nameEs: 'Documento General',
  description: 'Describe what you need — motion, memo, letter, declaration, notice, agreement, or any other legal document. Structure and filename are inferred from your description.',
  descriptionEs: 'Describe lo que necesitas — moón, memorándum, carta, declaración, aviso, acuerdo, o cualquier otro documento legal. La estructura y el nombre del archivo se infieren de tu descripción.',
  category: 'general',
  jurisdiction: 'california',
  // Miles decision #4 (2026-05-08): no attorney-review banner by default on
  // the new general-document path. Attorneys already know.
  requiresAttorneyReview: false,
  isFreeform: true,
  scope: 'static',
  fields: [
    {
      id: 'documentPurpose',
      label: 'What document do you need? (describe the purpose)',
      labelEs: '¿Qué documento necesitas? (describe el propósito)',
      type: 'textarea',
      required: true,
      placeholder: 'e.g. A motion to continue trial based on late-produced discovery; or a demand letter to a former employer for unpaid overtime; or a declaration in support of a U-visa application.',
      group: 'purpose',
    },
    {
      id: 'parties',
      label: 'Parties involved (optional)',
      labelEs: 'Partes involucradas (opcional)',
      type: 'textarea',
      required: false,
      placeholder: 'Names and roles of parties, witnesses, opposing counsel, etc.',
      group: 'context',
    },
    {
      id: 'jurisdiction',
      label: 'Jurisdiction / Forum (optional)',
      labelEs: 'Jurisdicción / Foro (opcional)',
      type: 'text',
      required: false,
      placeholder: 'e.g. Alameda County Superior Court, EOIR San Francisco, WCAB Oakland',
      group: 'context',
    },
    {
      id: 'keyTerms',
      label: 'Key terms, facts, or points to include (optional)',
      labelEs: 'Hechos clave, términos o puntos a incluir (opcional)',
      type: 'textarea',
      required: false,
      placeholder: 'Anything specific that must appear in the document — statutory citations, dates, dollar amounts, procedural posture, etc.',
      group: 'context',
    },
    {
      id: 'deadline',
      label: 'Deadline / Filing date (optional)',
      labelEs: 'Plazo / Fecha de presentación (opcional)',
      type: 'text',
      required: false,
      placeholder: 'e.g. must be filed by 2026-06-15',
      group: 'context',
    },
    {
      id: 'attachedDocuments',
      label: 'Attached source documents (optional)',
      labelEs: 'Documentos fuente adjuntos (opcional)',
      type: 'textarea',
      required: false,
      placeholder: 'Filled automatically from the multi-doc manifest when files are attached.',
      group: 'context',
    },
  ],
  sections: [
    {
      id: 'freeform',
      title: 'Document',
      titleEs: 'Documento',
      content: '',
      contentEs: '',
    },
  ],
};

// ============================================================================
// Attorney-review defaults for the 8 legacy templates
//
// Preserve current behavior: the existing 8 templates always show the
// attorney-review banner. Miles's decision #4 (2026-05-08) only applies to
// the new general-document path and user-saved reusable templates. We stamp
// requiresAttorneyReview: true at export time so we don't have to edit each
// legacy template definition block.
// ============================================================================

function applyLegacyAttorneyReviewDefault(tpl: LegalTemplate): LegalTemplate {
  if (tpl.requiresAttorneyReview !== undefined) return tpl;
  return { ...tpl, requiresAttorneyReview: true };
}

// ============================================================================
// Exports
// ============================================================================

export const LEGAL_TEMPLATES: LegalTemplate[] = [
  applyLegacyAttorneyReviewDefault(employmentAgreementTemplate),
  applyLegacyAttorneyReviewDefault(ndaTemplate),
  applyLegacyAttorneyReviewDefault(contractorAgreementTemplate),
  applyLegacyAttorneyReviewDefault(qmeAdvocacyMemoTemplate),
  applyLegacyAttorneyReviewDefault(wcabPreHearingStatementTemplate),
  applyLegacyAttorneyReviewDefault(immigrationPreHearingStatementTemplate),
  applyLegacyAttorneyReviewDefault(i589AsylumTemplate),
  applyLegacyAttorneyReviewDefault(demandLetterTemplate),
  // 9th template: general freeform drafter (Miles 2026-05-08)
  generalLegalDocumentTemplate,
];

export function getTemplateById(id: string): LegalTemplate | undefined {
  return LEGAL_TEMPLATES.find(t => t.id === id);
}

export function getTemplateByName(name: string): LegalTemplate | undefined {
  const lower = name.toLowerCase();
  return LEGAL_TEMPLATES.find(t =>
    t.id === lower ||
    t.name.toLowerCase().includes(lower) ||
    t.nameEs.toLowerCase().includes(lower)
  );
}
